import { FormConfig } from './types';
import { PAR_ID_MAX_LENGTH } from '../../../../../utils/constants';

/**
 * Form template for "Price Changes and Offers" moment type.
 *
 * Derived from Figma node 10282:1124522 and the backend implementation
 * guide (PRICE_CHANGES_AND_OFFERS_IMPLEMENTATION.md).
 *
 * Key differences from the legacy "Offer, Price Change, or Program":
 *  - PAR ID replaces GenAI / Consumes Credits
 *  - Dynamic RTM (Route-to-Market) phases instead of flat dates
 *  - No MVP Status, Zen, strategicAlignment in Overview
 *  - Milestones are moment-level (not per phase) — handled in the slideout
 *  - Tier lives at the moment level, not inside each phase
 */
export const priceChangesAndOffers: FormConfig = {
  dialogSize: 'large',
  sections: [
    // ── Top row: type picker + name ────────────────────────────
    {
      columns: 2,
      heading: '',
      subheading: '',
      fields: [
        {
          id: 'momentType',
          name: 'momentType',
          label: 'TYPE OF RELEASE OR MOMENT',
          type: 'select',
          required: true,
          options: [
            { value: 'New 1.0 Release', label: 'New 1.0 Release' },
            {
              value: 'Product Release with Marketing',
              label: 'Product Release with Marketing'
            },
            {
              value: 'Product Release without Marketing',
              label: 'Product Release without Marketing'
            },
            { value: 'Marketing Moment', label: 'Marketing Moment' },
            {
              value: 'Offer, Price Change, or Program',
              label: 'Offer, Price Change, or Program'
            },
            {
              value: 'Price Changes and Offers',
              label: 'Price Changes and Offers'
            },
            { value: 'Key Date', label: 'Key Date' },
            { value: 'Event', label: 'Event' }
          ]
        },
        {
          id: 'name',
          name: 'name',
          label: 'NAME OF RELEASE OR MOMENT',
          type: 'text',
          required: true
        }
      ]
    },

    // ── PAR ID + Tier ──────────────────────────────────────────
    {
      columns: 2,
      heading: '',
      subheading: '',
      fields: [
        {
          id: 'parId',
          name: 'parId',
          label: 'PRICING OPERATIONS PAR ID',
          type: 'text',
          required: true,
          maxLength: PAR_ID_MAX_LENGTH
        },
        {
          id: 'tier',
          name: 'tier',
          label: 'TIER',
          type: 'select',
          required: true,
          options: [
            { value: 'L1', label: 'L1' },
            { value: 'L2', label: 'L2' },
            { value: 'L3', label: 'L3' },
            { value: 'Other', label: 'Other' },
            { value: 'TBD', label: 'TBD' }
          ]
        }
      ]
    },

    // ── General Information ────────────────────────────────────
    {
      columns: 2,
      heading: 'General Information',
      subheading: '',
      required: true,
      fields: [
        {
          id: 'products',
          name: 'products',
          label: 'PRODUCT',
          type: 'multi_select_api',
          required: true,
          api: 'product-hierarchy-filters',
          dependents: ['categories', 'productGroups', 'customerAudiences']
        },
        {
          id: 'categories',
          name: 'categories',
          label: 'CATEGORY',
          type: 'multi_select_api',
          required: false,
          api: 'product-hierarchy-filters',
          dependsOn: 'products'
        },
        {
          id: 'productGroups',
          name: 'productGroups',
          label: 'PRODUCT GROUP',
          type: 'multi_select_api',
          required: true,
          api: 'product-hierarchy-filters',
          dependsOn: 'products'
        },
        {
          id: 'customerAudiences',
          name: 'customerAudiences',
          label: 'AUDIENCE',
          type: 'multi_select_api',
          required: false,
          api: 'product-hierarchy-filters',
          dependsOn: 'products'
        }
      ]
    },

    // ── Route-to-Market Phases (dynamic: 1–4 rows) ────────────
    {
      columns: 1,
      heading: 'Route to Market Phases',
      subheading:
        'Add 1 to 4 route-to-market phases. Each RTM can only be used once.',
      required: true,
      fields: [
        {
          id: 'rtmPhases',
          name: 'rtmPhases',
          label: '',
          type: 'repeatable_group_component',
          required: true,
          minItems: 1,
          maxItems: 4,
          fieldGroup: [
            {
              id: 'routeToMarket',
              name: 'routeToMarket',
              label: 'Route to Market',
              type: 'select',
              required: true,
              options: [
                { value: 'VIP', label: 'VIP' },
                { value: 'Mobile', label: 'Mobile' },
                { value: 'ETLA', label: 'ETLA' },
                { value: 'A.com Direct', label: 'A.com Direct' }
              ]
            },
            {
              id: 'customerNotificationDate',
              name: 'customerNotificationDate',
              label: 'CUSTOMER NOTIFICATION DATE',
              type: 'datepicker',
              required: true,
              disableNA: true,
              defaultValue: ''
            },
            {
              id: 'effectiveDate',
              name: 'effectiveDate',
              label: 'EFFECTIVE DATE',
              type: 'datepicker',
              required: false,
              defaultValue: ''
            }
          ]
        }
      ]
    },

    // ── Primary Contacts ───────────────────────────────────────
    {
      columns: 2,
      heading: 'Primary Contacts',
      subheading: 'Minimum 1 Primary Contact is Required',
      required: true,
      fields: [
        {
          id: 'programManagers',
          name: 'programManagers',
          label: 'PROGRAM MANAGER',
          type: 'ldap',
          required: true,
          minItems: 1
        },
        {
          id: 'productMarketingManagers',
          name: 'productMarketingManagers',
          label: 'PROD MKTG MANAGER',
          type: 'ldap',
          required: true,
          minItems: 1
        },
        {
          id: 'productManagers',
          name: 'productManagers',
          label: 'PRODUCT MANAGER',
          type: 'ldap',
          required: true,
          minItems: 1
        }
      ]
    },

    // ── Overview ───────────────────────────────────────────────
    {
      heading: 'Overview',
      subheading: '',
      fields: [
        {
          id: 'goal',
          name: 'goal',
          label: 'WHY ARE WE DOING THIS',
          type: 'select',
          required: true,
          columns: 2,
          options: [
            { value: 'Growth & Awareness', label: 'Growth & Awareness' },
            { value: 'Engagement & Retention', label: 'Engagement & Retention' }
          ]
        },
        {
          id: 'audienceStrategyMappings',
          name: 'audienceStrategyMappings',
          label: 'STRATEGIC ALIGNMENT',
          type: 'multi_select_api',
          required: true,
          columns: 2,
          api: 'product-hierarchy-filters',
          dependsOn: 'customerAudiences',
          grouped: true,
          subtitle: 'Required for L1 and L2'
        },
        {
          id: 'whatWeAreLaunching',
          name: 'whatWeAreLaunching',
          label: 'WHAT ARE WE LAUNCHING',
          type: 'richtext',
          required: true
        },
        {
          id: 'whereItWillLaunch',
          name: 'whereItWillLaunch',
          label: 'WHERE WILL IT LAUNCH',
          type: 'richtext',
          required: true
        }
      ]
    }
  ]
};
